import { InteractiveStatCard } from '@/components/dashboard/stats/interactive-stat-card';
import { TaskChart } from '@/components/dashboard/task-chart';
import { Building2, Clock, CheckCircle2, MessageSquare, Home, DollarSign, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';

export function PropertyOwnerDashboard() {
  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-primary-700 dark:text-primary-300">
              Owner Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your properties and service requests
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="text-sm px-4 py-2 rounded-full glass-effect text-muted-foreground flex items-center gap-2"
          >
            <DollarSign className="h-4 w-4 text-primary" />
            <span>Portfolio Value: $2.5M</span>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="text-sm px-4 py-2 rounded-full glass-effect text-muted-foreground flex items-center gap-2"
          >
            <Star className="h-4 w-4 text-primary" />
            <span>Service Rating: 4.8/5</span>
          </motion.div>
        </div>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <InteractiveStatCard
          title="Active Requests"
          value="12"
          description="+3 from last week"
          icon={Clock}
          trend={{
            data: [
              { name: 'Mon', value: 8 },
              { name: 'Tue', value: 10 },
              { name: 'Wed', value: 12 },
              { name: 'Thu', value: 9 },
              { name: 'Fri', value: 12 },
            ],
            description: "Service request activity over the past week"
          }}
          details={{
            description: "Current service requests status",
            items: [
              { label: "Pending Approval", value: "5" },
              { label: "In Progress", value: "4" },
              { label: "Scheduled", value: "3" },
              { label: "Average Response Time", value: "4h" }
            ]
          }}
        />
        <InteractiveStatCard
          title="Properties"
          value="5"
          description="All active"
          icon={Building2}
          details={{
            description: "Your property portfolio overview",
            items: [
              { label: "Total Value", value: "$2.5M" },
              { label: "Monthly Maintenance", value: "$8,500" },
              { label: "Upcoming Inspections", value: "2" },
              { label: "Service Contracts", value: "3" }
            ]
          }}
        />
        <InteractiveStatCard
          title="Completed Tasks"
          value="48"
          description="+12% this month"
          icon={CheckCircle2}
          trend={{
            data: [
              { name: 'Week 1', value: 10 },
              { name: 'Week 2', value: 12 },
              { name: 'Week 3', value: 15 },
              { name: 'Week 4', value: 11 },
            ],
            description: "Task completion trend"
          }}
          details={{
            description: "Maintenance task statistics",
            items: [
              { label: "Preventive", value: "28" },
              { label: "Repairs", value: "15" },
              { label: "Improvements", value: "5" },
              { label: "Cost Savings", value: "$3,200" }
            ]
          }}
        />
        <InteractiveStatCard
          title="Service Rating"
          value="4.8"
          description="Based on 32 reviews"
          icon={MessageSquare}
          trend={{
            data: [
              { name: 'Jan', value: 4.6 },
              { name: 'Feb', value: 4.7 },
              { name: 'Mar', value: 4.8 },
              { name: 'Apr', value: 4.8 },
            ],
            description: "Service quality ratings over time"
          }}
          details={{
            description: "Service quality metrics",
            items: [
              { label: "Service Quality", value: "4.8/5" },
              { label: "Response Time", value: "4.7/5" },
              { label: "Value for Money", value: "4.6/5" },
              { label: "Communication", value: "4.9/5" }
            ]
          }}
        />
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid gap-6 md:grid-cols-7"
      >
        <Card variant="glass" className="col-span-4 p-1 overflow-hidden">
          <TaskChart />
        </Card>
        
        <Card variant="glass" className="col-span-3 p-6 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Upcoming Maintenance</h3>
            <Button variant="glass" size="sm">View All</Button>
          </div>
          
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + (i * 0.1) }}
                className="flex items-start gap-3 p-3 rounded-lg bg-background/30 backdrop-blur-sm"
              >
                <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                  <Home className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <p className="font-medium text-sm">Scheduled Inspection</p>
                    <p className="text-xs bg-primary/10 px-2 py-0.5 rounded text-primary">
                      {new Date(Date.now() + i * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">Property: {i === 1 ? 'Riverside Apartments' : i === 2 ? 'Highland Towers' : 'Oakwood Estates'}</p>
                  <p className="text-xs text-primary/80 mt-1">Technician: {i === 1 ? 'John Smith' : i === 2 ? 'Sarah Johnson' : 'Mike Davis'}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      </motion.div>
    </div>
  );
}